// ABOUTME: Completion step for the DataSourceWizard.
// ABOUTME: Shows success animation and CTA to start first investigation.

import { motion } from 'framer-motion';
import { Button } from '@/components/ui';
import type { DataSource } from '@/types/api';

interface CompleteStepProps {
  dataSource: DataSource;
  onComplete: () => void;
}

/**
 * Animated success checkmark with scale and fade animation.
 */
function AnimatedCheckmark() {
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
      className="w-24 h-24 rounded-full bg-success/10 flex items-center justify-center"
    >
      <motion.svg
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="w-12 h-12 text-success"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={3}
      >
        <motion.path
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M5 13l4 4L19 7"
        />
      </motion.svg>
    </motion.div>
  );
}

/**
 * Celebration confetti particles.
 */
function ConfettiParticle({ delay, x }: { delay: number; x: number }) {
  return (
    <motion.div
      initial={{ y: -20, x, opacity: 0, rotate: 0 }}
      animate={{
        y: 100,
        opacity: [0, 1, 1, 0],
        rotate: 360,
      }}
      transition={{
        duration: 1.5,
        delay,
        ease: 'easeOut',
      }}
      className="absolute top-0 w-2 h-2 rounded-full bg-primary-400"
    />
  );
}

export function CompleteStep({ dataSource, onComplete }: CompleteStepProps) {
  return (
    <div className="flex flex-col items-center text-center px-6 py-8 relative overflow-hidden">
      {/* Confetti effect */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <ConfettiParticle
            key={i}
            delay={0.1 * i}
            x={(i - 2.5) * 40}
          />
        ))}
      </div>

      <AnimatedCheckmark />

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="text-2xl font-semibold text-surface-900 mt-6 mb-2">
          Data Source Created
        </h2>

        <p className="text-surface-600 mb-6 max-w-md">
          <strong className="text-surface-800">{dataSource.name}</strong> is now connected
          and ready for investigations.
        </p>
      </motion.div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="bg-surface-50 rounded-lg p-4 mb-8 w-full max-w-sm"
      >
        <h3 className="text-sm font-medium text-surface-700 mb-3">
          Connection Details
        </h3>
        <div className="text-sm text-surface-600 space-y-1 text-left">
          <div className="flex justify-between">
            <span className="text-surface-500">Type:</span>
            <span className="font-medium capitalize">{dataSource.type}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-500">Catalog:</span>
            <span className="font-medium">{dataSource.config?.catalog || (dataSource as unknown as Record<string, string>).catalog || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-500">Schema:</span>
            <span className="font-medium">{dataSource.config?.schema || (dataSource as unknown as Record<string, string>).schema_name || '-'}</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <Button size="lg" onClick={onComplete}>
          Start Your First Investigation
        </Button>
      </motion.div>
    </div>
  );
}
